import { FunctionComponent, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L, { Layer } from 'leaflet';
import { FeatureCollection, Geometry } from 'geojson';
import { includeColor } from 'utils/geometry';
import {
  DepartmentProperties,
  FeatureDepartment,
} from './interventions-field-repository';

const getDepartmentStyle = (status?: DepartmentProperties['status']) => {
  switch (status) {
    case 'selected':
      return {
        color: includeColor,
        fillColor: includeColor,
        fillOpacity: 0.15,
        weight: 3,
      };
    case 'partial':
      return {
        color: includeColor,
        fillColor: includeColor,
        fillOpacity: 0.05,
        weight: 3,
        dashArray: '8 6',
      };
    default:
      return {
        color: '#555555',
        fillColor: '#555555',
        fillOpacity: 0.05,
        weight: 2,
      };
  }
};

type InterventionsFieldDepartmentsLayerProps = {
  departments: FeatureCollection<Geometry, DepartmentProperties>;
  onEachDepartmentFeature: (feature: FeatureDepartment, layer: Layer) => void;
};

// The layer is added to the map directly instead of being a child of <Map />
// so that the map doesn't fit its bounds on the whole France
const InterventionsFieldDepartmentsLayer: FunctionComponent<
  InterventionsFieldDepartmentsLayerProps
> = ({ departments, onEachDepartmentFeature }) => {
  const map = useMap();

  useEffect(() => {
    const layer = L.geoJSON(departments, {
      pmIgnore: true,
      style: (feature) => getDepartmentStyle(feature?.properties?.status),
      onEachFeature: (feature, featureLayer) => {
        const { name, code, selected, total } = feature.properties;

        featureLayer.bindTooltip(
          `${name} (${code}) - ${selected}/${total} villes`,
          { sticky: true, opacity: 1 },
        );
        onEachDepartmentFeature(feature as FeatureDepartment, featureLayer);
      },
    });

    layer.addTo(map);

    // Draw the departments outline over the cities, which are added after
    const timeout = setTimeout(() => layer.bringToFront());

    return () => {
      clearTimeout(timeout);
      layer.remove();
    };
  }, [map, departments, onEachDepartmentFeature]);

  return null;
};

export default InterventionsFieldDepartmentsLayer;
